import { useEffect } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { SelectField } from '@brighte/form-components';
import { IncomeVerificationType } from '@brighte/validation';
import { AssistantFormFields, useAssistantSafe } from '@components/Assistant';
import { INCOME_VERIFICATION, LABELS, PLACEHOLDER } from '../../utils';

type RetiredOrPensionerProps = Pick<UseFormReturn<IncomeVerificationType>, 'control'>;

// The field path doubles as the assistant's identifier for this question.
const PENSION_TYPE_FIELD = 'applicant.employment.pensioner.pensionType' as const;

function RetiredOrPensioner({ control }: RetiredOrPensionerProps) {
  const { PENSION_TYPE } = LABELS;
  const { PENSIONER_OPTIONS } = INCOME_VERIFICATION;

  // Knowing the pension type lets the assistant name the one document this
  // customer needs. The FAQ gives three different answers for pension income
  // (pension statement / Centrelink statement / superannuation statement), and
  // without this it has to read out all three.
  const pensionType = useWatch({ control, name: PENSION_TYPE_FIELD });
  const reportDraftSelections = useAssistantSafe()?.reportDraftSelections;
  useEffect(() => {
    reportDraftSelections?.({ [PENSION_TYPE_FIELD]: pensionType });
    // A question that has left the screen is no longer an answer. React runs this
    // cleanup before the next effect, so a changed value retracts then re-sets;
    // on unmount it just retracts.
    return () => reportDraftSelections?.({ [PENSION_TYPE_FIELD]: undefined });
  }, [pensionType, reportDraftSelections]);

  return (
    <>
      <AssistantFormFields fields={[PENSION_TYPE_FIELD]} />
      <SelectField
        data={{ testid: 'retired-or-pensioner-select' }}
        name={PENSION_TYPE_FIELD}
        control={control}
        label={PENSION_TYPE}
        options={PENSIONER_OPTIONS}
        placeholder={PLACEHOLDER}
      />
    </>
  );
}

export default RetiredOrPensioner;
